<template>
  <div class="card">
    <div class="card-header">
      <h5 class="mb-0"><FolderCog :size="20" class="me-2" />File Storage Configuration</h5>
    </div>
    <div class="card-body">
      <div v-if="loadState.IsLoading" class="text-center py-4"><i class="fas fa-spinner fa-spin me-2"></i>Loading settings...</div>
      <form v-else @submit.prevent="saveSettings">
        <div class="alert alert-info">Configure the FTP or FTPS provider used to store app attachments.</div>
        <div class="row">
          <div class="col-md-8 mb-3">
            <label class="form-label">Host</label>
            <input class="form-control" v-model.trim="settings.Host" placeholder="ftp://files.example.com" required />
          </div>
          <div class="col-md-4 mb-3">
            <label class="form-label">Port</label>
            <input class="form-control" type="number" min="1" max="65535" v-model.number="settings.Port" required />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Username</label>
            <input class="form-control" v-model.trim="settings.Username" autocomplete="off" required />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Password</label>
            <div class="input-group">
              <input class="form-control" :type="showPassword ? 'text' : 'password'" v-model="settings.Password" autocomplete="new-password" required />
              <button type="button" class="btn btn-outline-secondary" @click="showPassword = !showPassword">
                <EyeOff v-if="showPassword" :size="18" /><Eye v-else :size="18" />
              </button>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Base Directory</label>
            <input class="form-control" v-model.trim="settings.BaseDir" placeholder="RadarAssets" />
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Timeout (ms)</label>
            <input class="form-control" type="number" min="1000" v-model.number="settings.TimeoutMilliseconds" required />
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Passive Attempts</label>
            <input class="form-control" type="number" min="1" v-model.number="settings.MaxAttempts" required />
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Encryption Mode</label>
            <select class="form-select" v-model="settings.EncryptionMode">
              <option value="None">None</option>
              <option value="Auto">Auto</option>
              <option value="Explicit">Explicit FTPS</option>
              <option value="Implicit">Implicit FTPS</option>
            </select>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">SSL Protocol</label>
            <select class="form-select" v-model="settings.SslProtocol">
              <option value="None">None</option>
              <option value="Tls">TLS</option>
              <option value="Tls11">TLS 1.1</option>
              <option value="Tls12">TLS 1.2</option>
              <option value="Tls13">TLS 1.3</option>
            </select>
          </div>
          <div class="col-12 mb-3">
            <div class="form-check">
              <input id="validateAnyCertificate" class="form-check-input" type="checkbox" v-model="settings.ValidateAnyCertificate" />
              <label class="form-check-label" for="validateAnyCertificate">Accept any server certificate</label>
            </div>
            <small class="text-muted">Only enable this when your provider requires a certificate that cannot be validated normally.</small>
          </div>
          <div class="col-12 mb-3">
            <label class="form-label">Part Receipts / Attachments Directory</label>
            <input class="form-control" v-model.trim="settings.PartDir" placeholder="/Shared Folders/2026 Technician Receipts/" />
            <small class="text-muted">Directory on the FTP host where part receipts and attachments are stored. Start with / for an absolute path, or omit it to store under the base directory. Leave blank for the default location. Update yearly as needed.</small>
          </div>
        </div>
        <div v-if="saveState.IsError" class="alert alert-danger py-2">{{ saveState.Message }}</div>
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-outline-secondary" :disabled="testState.IsLoading || saveState.IsLoading" @click="testConnection">
            <Zap :size="16" class="me-1" />{{ testState.IsLoading ? 'Testing...' : 'Test Connection' }}
          </button>
          <button type="submit" class="btn btn-primary" :disabled="saveState.IsLoading">
            <Save :size="16" class="me-1" />{{ saveState.IsLoading ? 'Saving...' : 'Save Settings' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { Eye, EyeOff, FolderCog, Save, Zap } from 'lucide-vue-next';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { useApiRequest } from '@/composables';
import type { FtpSettings } from '@/models/AttachmentModel';

const defaults = (): FtpSettings => ({
  Host: '',
  Port: 21,
  Username: '',
  Password: '',
  BaseDir: 'RadarAssets',
  TimeoutMilliseconds: 60000,
  EncryptionMode: 'Implicit',
  SslProtocol: 'Tls12',
  ValidateAnyCertificate: true,
  MaxAttempts: 10,
  PartDir: ''
});

const settings = ref<FtpSettings>(defaults());
const showPassword = ref(false);
const { state: loadState, execute: load } = useApiRequest<FtpSettings>('files:settings:load');
const { state: saveState, execute: save } = useApiRequest<null>('files:settings:save');
const { state: testState, execute: test } = useApiRequest<null>('files:settings:test');

const loadSettings = async () => {
  await load(() => ApiService.getFtpSettings());
  if (loadState.value.Data) settings.value = { ...defaults(), ...loadState.value.Data };
};

const saveSettings = async () => {
  await save(() => ApiService.saveFtpSettings(settings.value));
  if (saveState.value.IsError) Alert.error(saveState.value.Message || 'Unable to save file settings');
  else Toast.success('File settings saved successfully!');
};

const testConnection = async () => {
  await save(() => ApiService.saveFtpSettings(settings.value));
  if (saveState.value.IsError) {
    Alert.error(saveState.value.Message || 'Unable to save file settings');
    return;
  }
  await test(() => ApiService.testFtpConnection());
  if (testState.value.IsError) Alert.error(testState.value.Message || 'FTP connection failed');
  else Toast.success('FTP connection successful!');
};

onMounted(loadSettings);
</script>